import { createApp } from 'vue'
import router from './router/index'
import store from './store'
import appStore from './store/appStore'
import { storeToRefs } from 'pinia'
// @ts-ignore
import kmartSdk from './hooks/kmart_sdk'

// ========== 全局错误捕获：防止 JS 错误导致白屏 ==========
window.onerror = (message, source, lineno, colno, error) => {
	console.error('[Global Error]', { message, source, lineno, colno, error })
	// 确保页面不会因为 JS 错误而完全崩溃
	return false
}
window.addEventListener('unhandledrejection', (event) => {
	console.error('[Unhandled Promise Rejection]', event.reason)
})
// ======================================================
import 'vant/lib/index.css';
import "vant/es/toast/style"	// 引入这个是为了处理同一组件引入popup导致toast背景变白的bug
import "./assets/iconfont/iconfont.css"
import './assets/tailwind.css'
import './assets/font/font.scss'
import './style.scss'
import './assets/common.scss'
import { Locale } from 'vant';
import enUS from 'vant/es/locale/lang/en-US';	// 引入英文语言包
import zhCN from 'vant/es/locale/lang/zh-CN';	// 引入中文语言包
import jaJP from 'vant/es/locale/lang/ja-JP';	// 引入日文语言包
import koKR from 'vant/es/locale/lang/ko-KR';	// 引入韩文语言包
import { createI18n } from 'vue-i18n'
import en from './assets/locales/en'
import zh from './assets/locales/zh'
import ja from './assets/locales/ja'
import ko from './assets/locales/ko'
import App from './App.vue'
kmartSdk()
const app = createApp(App)
app.use(router).use(store)
const { appInfo, lang } = storeToRefs(appStore())

const i18n = createI18n({
	legacy: false,
	locale: 'en', // 默认语言，后续会在 setLang 中更新
	messages: {
		'en': en,
		'zh-CN': zh,
		'ja-JP': ja,
		'ko-KR': ko,
	},
})
app.use(i18n)

/**
 * @name 设置语言
 * @description i18n国际化
 * @tips 默认en(英语),zh-cn(中文),ja-JP(日语),ko-KR(韩语)
 */
const setLang = () => {
	const arr: any = [{ lang: 'en', locale: enUS, vantLang: 'en-US' }, { lang: 'zh-CN', locale: zhCN, vantLang: 'zh-CN' }, { lang: 'ja-JP', locale: jaJP, vantLang: 'ja-JP' }, { lang: 'ko-KR', locale: koKR, vantLang: 'ko-KR' }] // 这个是客户端的语言列表
	let index = arr.findIndex((item: any) => item.lang === lang.value)
	if (index === -1) {
		lang.value = 'en'
		index = arr.findIndex((item: any) => item.lang === 'en')
	} else {
		lang.value = arr[index].lang
	}
	// 更新 i18n 的 locale
	i18n.global.locale.value = arr[index].lang as 'en' | 'zh-CN' | 'ja-JP' | 'ko-KR'
	Locale.use(arr[index].vantLang, arr[index].locale);
}
// 获取设备信息
const kmartHost = (window as any).kmartApp

// 全局导航守卫（需在挂载前注册）
router.beforeEach((_to, _from, next) => {
	next()
})

/** 在 i18n 就绪后挂载应用，避免 APP 内白屏（SDK 无响应时也能在超时后挂载） */
const I18N_MOUNT_TIMEOUT_MS = 3000

const doMount = () => {
	app.mount('#app')
	// 标记已挂载，让 index.html 中的兜底脚本知道
	;(window as any).__appMounted = true
}

const initI18nAndMount = () => {
	setLang()
	doMount()
}

if (kmartHost) {
	let mounted = false
	const tryMount = () => {
		if (mounted) return
		mounted = true
		initI18nAndMount()
	}
	// 超时保护：先启动定时器，确保即使 SDK 调用抛异常也能挂载
	setTimeout(tryMount, I18N_MOUNT_TIMEOUT_MS)
	// 优先用 APP 返回的语言初始化 i18n 再挂载
	try {
		if (typeof kmartHost.device?.info === 'function') {
			kmartHost.device.info((res: any) => {
				try {
					if (res?.data) {
						appInfo.value.deviceInfo = res.data
						lang.value = res.data.language || 'en'
					}
				} catch (e) {
					console.error('[main.ts] device.info callback error:', e)
				}
				tryMount()
			})
		} else {
			console.warn('[main.ts] kmartHost.device.info is not a function')
			tryMount()
		}
	} catch (e) {
		console.error('[main.ts] kmartHost.device.info call error:', e)
		tryMount()
	}
} else {
	initI18nAndMount()
}

// dev 环境下提供一个可在 Console 随意切换 App/Web 模式的开关
if (import.meta.env.DEV) {
	const w = window as any

	const installMockKmartApp = () => {
		if (w.kmartApp) return
		w.kmartApp = {
			nav: {
				closeFlutter: (url: string) => {
					console.log('[mock kmartApp.nav.closeFlutter]', url)
					w.alert?.(`[mock] closeFlutter: ${url || '(empty url)'}`)
				},
				login: (cb: any) => {
					console.log('[mock kmartApp.nav.login]')
					cb?.({ data: { result: true } })
				}
			},
			appbar: {
				setVisibility: (visible: boolean, cb: any) => {
					console.log('[mock kmartApp.appbar.setVisibility]', visible)
					cb?.({ result: true })
				}
			},
			creds: {
				get: (cb: any) => {
					console.log('[mock kmartApp.creds.get]')
					cb?.({ data: { token: '', info: {} } })
				}
			},
			device: {
				info: (cb: any) => {
					console.log('[mock kmartApp.device.info]')
					cb?.({ data: { language: lang.value || 'en' } })
				}
			},
			share: (...args: any[]) => console.log('[mock kmartApp.share]', args),
			cart: { sync: (cb: any) => cb?.() }
		}
	}

	const setMode = (mode: 'app' | 'web') => {
		if (mode === 'app') {
			installMockKmartApp()
			appInfo.value.isApp = true
		} else {
			appInfo.value.isApp = false
		}
		localStorage.setItem('__debug_mode__', mode)
	}

	// 初始化时按上次选择的模式恢复
	const mode = (localStorage.getItem('__debug_mode__') as 'app' | 'web') || 'web'
	setMode(mode)

	w.__DEBUG__ = {
		setMode: (mode: 'app' | 'web') => {
			setMode(mode)
			w.location.reload()
		},
		getMode: () => localStorage.getItem('__debug_mode__') || 'web'
	}

	console.log('==================== DEV DEBUG MODE ====================')
	console.log('[debug] current mode:', w.__DEBUG__.getMode())
	console.log("[debug] switch to APP : window.__DEBUG__.setMode('app')")
	console.log("[debug] switch to WEB : window.__DEBUG__.setMode('web')")
	console.log('========================================================')
}
